import { Helmet } from "react-helmet-async";
import { useI18n, languages } from "@/i18n";
import { getSeoCopy, seoPaths, type SeoRouteKey } from "@/i18n/seoCopy";

const BASE_URL = "https://www.aureum7trading.com";

/**
 * Sitewide business identity. No street address by owner rule —
 * Marbella / Spain only, area served: Spain.
 */
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  name: "Aureum VII Trading",
  url: BASE_URL,
  email: "info@aureum7trading.com",
  telephone: "+34 688 495 309",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Marbella",
    addressCountry: "ES",
  },
  areaServed: {
    "@type": "Country",
    name: "Spain",
  },
};

interface SeoHeadProps {
  /** Key of the route in src/i18n/seoCopy.ts — drives title, description, canonical and hreflang. */
  route: SeoRouteKey;
  /** Optional route-specific JSON-LD (Product, FAQPage, Article, ...). Organization is always emitted. */
  schema?: object | object[];
  noindex?: boolean;
  image?: string;
}

/**
 * Shared per-route head: <html lang/dir>, title, description, self-canonical,
 * reciprocal hreflang for all six locales + x-default, Open Graph/Twitter and
 * the clean Organization schema. Use this instead of <SEO> on the key routes.
 */
export const SeoHead = ({ route, schema, noindex = false, image }: SeoHeadProps) => {
  const { language, isRTL } = useI18n();
  const copy = getSeoCopy(route, language);
  const path = seoPaths[route];
  const url = `${BASE_URL}/${language}${path}`;
  const currentLocale = languages.find((l) => l.code === language)?.locale ?? "en-GB";
  const fullImage = image
    ? image.startsWith("http")
      ? image
      : `${BASE_URL}${image}`
    : undefined;

  const schemas = [organizationSchema, ...(schema ? (Array.isArray(schema) ? schema : [schema]) : [])];

  return (
    <Helmet>
      <html lang={language} dir={isRTL ? "rtl" : "ltr"} />
      <title>{copy.title}</title>
      <meta name="description" content={copy.description} />
      <link rel="canonical" href={url} />

      {/* Reciprocal hreflang for all supported locales + x-default to /en */}
      {languages.map((lang) => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.locale}
          href={`${BASE_URL}/${lang.code}${path}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en${path}`} />

      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />

      {/* Open Graph */}
      <meta property="og:title" content={copy.title} />
      <meta property="og:description" content={copy.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Aureum VII Trading" />
      <meta property="og:locale" content={currentLocale.replace("-", "_")} />
      {languages
        .filter((l) => l.code !== language)
        .map((l) => (
          <meta key={l.code} property="og:locale:alternate" content={l.locale.replace("-", "_")} />
        ))}
      {fullImage && <meta property="og:image" content={fullImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={copy.title} />
      <meta name="twitter:description" content={copy.description} />
      {fullImage && <meta name="twitter:image" content={fullImage} />}

      {/* Structured data: clean Organization + optional route-specific schema */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
